import { flip, offset, shift } from '@floating-ui/dom'
import {
  autoUpdate,
  FloatingPortal,
  Placement,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import { arrow, FloatingArrow } from '@floating-ui/react'
import { Box, useTheme } from '@mui/material'
import i18n from 'i18n'
import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import React from 'react'
import Logger from 'services/Logger'

export type PopupSurfaceLevel = 'primary' | 'secondary'

interface TFloatingPopoverProps {
  content: ReactNode
  anchorElement: HTMLElement | null
  open: boolean
  onClose: () => void
  id?: string
  closeOnTouchOutside?: boolean
  onOutsideClick?: () => void
  defaultPlacement?: 'left' | 'right' | 'top' | 'bottom'
  fallbackPlacements?: Placement[]
  rightClickClose?: boolean
  padding?: number
  borderRadius?: number | string
  popupBorder?: string
  surfaceLevel?: PopupSurfaceLevel
  closeOnEscape?: boolean
  animatePositionChanges?: boolean
  /**
   * Whether to show an arrow pointing to the anchor element (default: true)
   */
  showArrow?: boolean
  /**
   * Bump this number to force the popover to re-measure its anchor even
   * when the `anchorElement` reference is unchanged. Needed when the
   * underlying DOM node is repositioned via CSS (e.g. FullCalendar shifts
   * an event card when its start time changes but reuses the same node).
   */
  repositionVersion?: number
}

interface FrozenReferenceRect {
  x: number
  y: number
  top: number
  left: number
  right: number
  bottom: number
  width: number
  height: number
}

const TFloatingPopover = (props: TFloatingPopoverProps) => {
  const theme = useTheme()
  const {
    open,
    onClose,
    anchorElement,
    content,
    closeOnTouchOutside: closeOnTouchOutside = false,
    onOutsideClick,
    closeOnEscape: closeOnEscape = false,
    showArrow = true
  } = props

  const arrowRef = useRef<SVGSVGElement | null>(null)
  const [lastPosition, setLastPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const frozenReferenceRectRef = useRef<FrozenReferenceRect | null>(null)
  const previousAnchorRef = useRef<HTMLElement | null>(null)

  // The reference rect height we feed to floating-ui is capped to this
  // value. This prevents the FloatingArrow from drifting toward the bottom
  // of the popup when the underlying event card grows tall (e.g. the user
  // extends the event's end time). The arrow ends up pointing to a stable
  // band near the top of the event card.
  const REFERENCE_HEIGHT_CAP = 48

  const captureFrozenRect = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect()
    frozenReferenceRectRef.current = {
      x: rect.x,
      y: rect.y,
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height
    }
  }

  useEffect(() => {
    if (!open) {
      previousAnchorRef.current = anchorElement
      return
    }

    if (previousAnchorRef.current !== anchorElement) {
      frozenReferenceRectRef.current = null
      setLastPosition(null)
    }

    previousAnchorRef.current = anchorElement
  }, [open, anchorElement])

  useLayoutEffect(() => {
    if (!open) {
      frozenReferenceRectRef.current = null
      return
    }

    if (anchorElement && document.body.contains(anchorElement)) {
      captureFrozenRect(anchorElement)
      update()

      // FullCalendar can update the anchor's id attribute and its inline
      // style (`top`/`transform`) in separate commits, so a single update()
      // here may still read the previous position. Re-run update() across
      // several animation frames so the popover follows the event card
      // even when only the time changes (FC reuses the same DOM node).
      let rafId = 0
      let cancelled = false
      let elapsed = 0
      const tick = (timestamp: number, start: number) => {
        if (cancelled) return
        if (anchorElement && document.body.contains(anchorElement)) {
          captureFrozenRect(anchorElement)
          update()
        }
        elapsed = timestamp - start
        if (elapsed < 300) {
          rafId = requestAnimationFrame((t) => tick(t, start))
        }
      }
      rafId = requestAnimationFrame((t) => tick(t, t))

      return () => {
        cancelled = true
        cancelAnimationFrame(rafId)
      }
    }
  }, [open, anchorElement, props.repositionVersion])

  const referenceElement = useMemo(
    () => ({
      getBoundingClientRect: () => {
        const frozenRect = frozenReferenceRectRef.current

        if (anchorElement && document.body.contains(anchorElement)) {
          const rect = anchorElement.getBoundingClientRect()

          // If the live anchor isn't laid out yet (FullCalendar may have
          // just re-rendered it), fall back to the frozen rect so the
          // floating-ui middlewares don't compute a zero position.
          if (rect.width === 0 && rect.height === 0 && frozenRect) {
            return DOMRect.fromRect({
              x: frozenRect.x,
              y: frozenRect.y,
              width: frozenRect.width,
              height: Math.min(frozenRect.height, REFERENCE_HEIGHT_CAP)
            })
          }

          // Use the live position so the popover follows the event card
          // whenever it moves (start time change, scroll, etc.), but cap
          // the height so the arrow's vertical midpoint stays anchored
          // near the top of the event even when the card grows.
          return DOMRect.fromRect({
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: Math.min(rect.height, REFERENCE_HEIGHT_CAP)
          })
        }

        return frozenRect
          ? DOMRect.fromRect({
              x: frozenRect.x,
              y: frozenRect.y,
              width: frozenRect.width,
              height: Math.min(frozenRect.height, REFERENCE_HEIGHT_CAP)
            })
          : new DOMRect(0, 0, 0, 0)
      },
      get contextElement() {
        return anchorElement ?? undefined
      }
    }),
    // Recreate when the anchor changes so floating-ui's autoUpdate
    // (whileElementsMounted) re-subscribes to the new contextElement and
    // re-measures against the new element on the very next render.
    [anchorElement]
  )

  const isAnchorValid = !!anchorElement && document.body.contains(anchorElement)

  const { x, y, strategy, refs, context, placement, update, isPositioned } =
    useFloating({
      open,
      onOpenChange: onClose,
      elements: { reference: referenceElement as any },
      placement:
        props.defaultPlacement ?? (i18n.dir() === 'ltr' ? 'right' : 'left'),
      middleware: [
        offset({ mainAxis: 10, crossAxis: 0 }),
        flip({
          fallbackStrategy: 'initialPlacement',
          padding: 8,
          fallbackPlacements: props.fallbackPlacements ?? [
            props.defaultPlacement ?? (i18n.dir() === 'ltr' ? 'right' : 'left'),
            i18n.dir() === 'ltr' ? 'right' : 'left',
            'top'
          ]
        }),
        shift({ padding: 8 }),
        ...(showArrow ? [arrow({ element: arrowRef })] : [])
      ],
      whileElementsMounted: autoUpdate
    })

  useEffect(() => {
    if (!open || !anchorElement || !document.body.contains(anchorElement)) {
      return
    }

    let rafId = 0
    const syncPosition = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        if (anchorElement && document.body.contains(anchorElement)) {
          captureFrozenRect(anchorElement)
          update()
        }
      })
    }

    // With a virtual reference element, floating-ui's autoUpdate can miss
    // some viewport/ancestor scroll updates. Keep the popover attached to
    // the event by forcing a re-measure on scroll and resize.
    window.addEventListener('scroll', syncPosition, true)
    window.addEventListener('resize', syncPosition)

    return () => {
      window.removeEventListener('scroll', syncPosition, true)
      window.removeEventListener('resize', syncPosition)
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [open, anchorElement, update])

  // Preserve last position while open
  useEffect(() => {
    if (
      open &&
      frozenReferenceRectRef.current &&
      typeof x === 'number' &&
      typeof y === 'number' &&
      x !== 0 &&
      y !== 0
    ) {
      setLastPosition({ x, y })
    }
    if (!open) {
      setLastPosition(null)
    }
  }, [open, x, y])

  const { getFloatingProps } = useInteractions([
    useDismiss(context, {
      outsidePress: (event) => {
        return closeOnTouchOutside
      },
      escapeKey: closeOnEscape ?? false
    }),
    useRole(context, { role: 'tooltip' })
  ])

  const floatingProps = getFloatingProps({
    onClick: (event) => {
      event.stopPropagation()
    },
    onMouseDown: (event) => {
      event.stopPropagation()
    }
  })

  const isCutOff = x < 0

  const shouldHideUntilPositioned = isAnchorValid && !isPositioned
  const isDark = theme.palette.mode === 'dark'
  const isPrimarySurface = props.surfaceLevel !== 'secondary'
  const popupBorderColor = 'rgba(255, 255, 255, 0.3)'
  const popupBorder = props.popupBorder ?? `1px solid ${popupBorderColor}`
  const popupShadow = isDark
    ? isPrimarySurface
      ? '0px 8px 24px rgba(0, 0, 0, 0.28), 0px 14px 34px rgba(0, 0, 0, 0.34)'
      : '0px 10px 26px rgba(0, 0, 0, 0.34), 0px 18px 42px rgba(0, 0, 0, 0.42)'
    : '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 8px 40px rgba(0, 0, 0, 0.3)'

  const popoverStyle = useMemo(() => {
    const isTopPlacement = placement.startsWith('top')
    const frozenRect = frozenReferenceRectRef.current
    const shouldUseLastPosition =
      !!lastPosition && (!isAnchorValid || !frozenRect || (x === 0 && y === 0))

    if (shouldUseLastPosition) {
      return {
        top: lastPosition.y,
        left: lastPosition.x,
        position: 'fixed',
        borderRadius: props.borderRadius ?? '16px',
        boxShadow: popupShadow,
        padding: theme.spacing(props.padding ?? 2, 0),
        backgroundColor: theme.palette.card.primary,
        zIndex: 1300
      }
    }

    if (isCutOff) {
      return {
        top: y ?? 0,
        left: Math.max(x ?? 0, 8),
        position: 'fixed',
        borderRadius: props.borderRadius ?? '16px',
        boxShadow: popupShadow,
        padding: theme.spacing(props.padding ?? 2, 0),
        backgroundColor: theme.palette.card.primary,
        zIndex: 1300
      }
    }

    return {
      top: y ?? 0,
      left: x ?? 0,
      position: strategy,
      borderRadius: props.borderRadius ?? '16px',
      boxShadow: popupShadow,
      padding: theme.spacing(2, 0),
      backgroundColor: theme.palette.card.primary,
      zIndex: 1300,
      transform: isTopPlacement ? 'translateY(-5px)' : 'translateY(5px)'
    }
  }, [
    x,
    y,
    strategy,
    placement,
    isCutOff,
    isAnchorValid,
    lastPosition,
    props.borderRadius,
    props.padding,
    props.surfaceLevel,
    popupShadow,
    theme
  ])

  if (!open) return null

  return (
    <FloatingPortal>
      <div
        style={{
          display: closeOnTouchOutside || onOutsideClick ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.0)',
          zIndex: closeOnTouchOutside ? 1299 : onOutsideClick ? 999 : 1
        }}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()

          if (props.rightClickClose && onOutsideClick) {
            onOutsideClick()
          }
        }}
        onClick={
          closeOnTouchOutside ? (onOutsideClick ?? onClose) : onOutsideClick
        }
      />
      <Box
        ref={refs.setFloating}
        sx={{
          ...(popoverStyle as any),
          border: popupBorder,
          padding: theme.spacing(props.padding ?? 2, 0),
          visibility: shouldHideUntilPositioned ? 'hidden' : undefined,
          transition: props.animatePositionChanges
            ? theme.transitions.create(['top', 'left', 'transform'], {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut
              })
            : undefined
        }}
        {...floatingProps}
      >
        {content}

        {showArrow && !isCutOff && isAnchorValid && (
          <FloatingArrow
            width={20}
            height={12}
            stroke={popupBorderColor}
            strokeWidth={1}
            fill={theme.palette.card.primary}
            ref={arrowRef}
            context={context}
          />
        )}
      </Box>
    </FloatingPortal>
  )
}

export default React.memo(TFloatingPopover)
